import { useEffect, useRef, useState } from 'react';
import {
    dateToMonthKey,
    getLastCompletedFinancialYearRange,
    getLastCompletedMonthKey,
    getTrailing12MonthsRange,
    monthKeyToEndDate,
    monthKeyToStartDate,
} from '../../../../utils/dateUtils';

// RECUR-27384 review fix: named constants for the range-key values, so callers compare
// against these instead of hardcoded string literals.
export const GST_RANGE_KEYS = {
    ALL: 'ALL',
    LAST_FY: 'LAST_FY',
    LAST_12M: 'LAST_12M',
    CUSTOM: 'CUSTOM',
} as const;

export type GstRangeKey = (typeof GST_RANGE_KEYS)[keyof typeof GST_RANGE_KEYS];

interface FetchRange {
    startDate: string;
    endDate: string;
}

// GST rolled out in India in July 2017 — no GST-sourced data can predate this
// month, so this is the hard floor for both month pickers.
export const GST_EPOCH_MONTH = '2017-07';

/**
 * Local state for the GST dashboard's "Time Period" control (RECUR-27384).
 * Owns the applied range (ALL / LAST_FY / LAST_12M / CUSTOM) and derives both
 * the fetch payload dates and the two chip display months from it.
 *
 * `tab` resets the range back to ALL whenever it changes — the legacy
 * Dashboard/index.tsx path reuses a single GstAnalysisPanel instance across
 * the REVENUE and VENDOR_EXPENSES tabs, so without this the applied range
 * would otherwise leak from one tab into the other.
 */
export function useGstDateRange(tab?: string) {
    const [rangeKey, setRangeKey] = useState<GstRangeKey>(GST_RANGE_KEYS.ALL);
    // ALL holds both chips empty — no display-only trailing-12M seeding (RECUR-27384
    // Figma fidelity pass). The chips render a muted placeholder until a toggle or a
    // custom pick fills them; the fetch for ALL is unaffected (always '' / '', see
    // fetchRange below), so the backend keeps applying the per-PNO config window.
    const [customRange, setCustomRange] = useState({ startMonth: '', endMonth: '' });
    // The fetch range actually applied to charts/tables. A one-sided CUSTOM pick
    // (only start or only end month chosen) updates the chip display above via
    // customRange but must NOT touch this — monthKeyToStartDate/EndDate('') parse
    // to a bogus 2001 date, not an empty range (RECUR-27384 review fix). The
    // previous applied range stays in effect until both months are set.
    const [fetchRange, setFetchRange] = useState<FetchRange>({ startDate: '', endDate: '' });

    const maxMonth = getLastCompletedMonthKey();

    const applyRange = (key: GstRangeKey, range: FetchRange) => {
        setRangeKey(key);
        setCustomRange({ startMonth: dateToMonthKey(range.startDate), endMonth: dateToMonthKey(range.endDate) });
        setFetchRange(range);
    };

    const selectAll = () => applyRange(GST_RANGE_KEYS.ALL, { startDate: '', endDate: '' });
    const selectLastFY = () => applyRange(GST_RANGE_KEYS.LAST_FY, getLastCompletedFinancialYearRange());
    const selectLast12M = () => applyRange(GST_RANGE_KEYS.LAST_12M, getTrailing12MonthsRange());

    const selectStartMonth = (month: string) => {
        const nextRange = { ...customRange, startMonth: month };
        setRangeKey(GST_RANGE_KEYS.CUSTOM);
        setCustomRange(nextRange);
        if (nextRange.endMonth) {
            setFetchRange({ startDate: monthKeyToStartDate(nextRange.startMonth), endDate: monthKeyToEndDate(nextRange.endMonth) });
        }
    };
    const selectEndMonth = (month: string) => {
        const nextRange = { ...customRange, endMonth: month };
        setRangeKey(GST_RANGE_KEYS.CUSTOM);
        setCustomRange(nextRange);
        if (nextRange.startMonth) {
            setFetchRange({ startDate: monthKeyToStartDate(nextRange.startMonth), endDate: monthKeyToEndDate(nextRange.endMonth) });
        }
    };

    const isTabMountRef = useRef(true);
    useEffect(() => {
        if (isTabMountRef.current) {
            isTabMountRef.current = false;
            return;
        }
        selectAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    return {
        rangeKey,
        fetchStartDate: fetchRange.startDate,
        fetchEndDate: fetchRange.endDate,
        chipStartMonth: customRange.startMonth,
        chipEndMonth: customRange.endMonth,
        isRangeActive: rangeKey !== GST_RANGE_KEYS.ALL,
        // Start picker: floor at GST epoch, ceiling at the currently chosen end month.
        startPickerMin: GST_EPOCH_MONTH,
        startPickerMax: customRange.endMonth || maxMonth,
        // End picker: floor at the currently chosen start month, ceiling at last completed month.
        endPickerMin: customRange.startMonth || GST_EPOCH_MONTH,
        endPickerMax: maxMonth,
        selectAll,
        selectLastFY,
        selectLast12M,
        selectStartMonth,
        selectEndMonth,
        reset: selectAll,
    };
}
